import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
interface HeroScreen {
  title: string;
  src: string;
  alt: string;
}
const screens: HeroScreen[] = [{
  title: 'Coach Hub',
  src: 'https://cdn.magicpatterns.com/uploads/sJkEmhcRbSrPkcNfUn2TgB/image.png',
  alt: 'The Nisa Coach Hub. An Ask Nisa prompt sits above a roster of teacher cards, each summarizing that teacher’s most recent classroom visit and current coaching focus.'
}, {
  title: 'Teacher Profile',
  src: 'https://cdn.magicpatterns.com/uploads/gf1H4UUAZ7DEytzZPkdGA6/image.png',
  alt: 'A teacher profile in Nisa. A summary of what Nisa knows and the current cycle goal sit above the Growth Record, a dated list of observations, coaching notes, and recordings.'
}, {
  title: 'Goal Workspace',
  src: 'https://cdn.magicpatterns.com/uploads/t7evkfj8YrrWKSWiTC7uwz/image.png',
  alt: 'A goal workspace in Nisa for a student participation goal, listing the agreed action steps alongside the progress signals gathered against them.'
}];

// A cursor crossing the hero on its way somewhere else should not start the
// cycle. Wait long enough to tell passing from looking.
const HOVER_INTENT_MS = 220;

// The three screens share the same app chrome, so a dissolve holds the frame
// steady and lets only the content change. A slide made hundreds of small UI
// elements travel at once, which read as noise rather than as a transition.
const imageTransition = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1] as const
};
const captionTransition = {
  duration: 0.3,
  ease: [0.22, 1, 0.36, 1] as const
};
export const HeroProductCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isEngaged, setIsEngaged] = useState(false);
  const [canHover, setCanHover] = useState(true);
  const intentTimer = useRef<number | null>(null);
  const shouldReduceMotion = useReducedMotion();
  const activeScreen = screens[activeIndex];
  useEffect(() => {
    screens.slice(1).forEach(({
      src
    }) => {
      const image = new Image();
      image.src = src;
    });
  }, []);

  // Hover is the desktop affordance. Touch devices never fire it, so they get a
  // slower continuous cycle instead of a frozen first screen.
  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setCanHover(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  useEffect(() => () => {
    if (intentTimer.current) window.clearTimeout(intentTimer.current);
  }, []);
  useEffect(() => {
    if (shouldReduceMotion) {
      return undefined;
    }
    const isPlaying = canHover ? isEngaged : true;
    if (!isPlaying) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      setActiveIndex(currentIndex => (currentIndex + 1) % screens.length);
    }, canHover ? 3200 : 4800);
    return () => window.clearInterval(intervalId);
  }, [canHover, isEngaged, shouldReduceMotion]);
  const handleMouseEnter = () => {
    if (!canHover || shouldReduceMotion) return;
    if (intentTimer.current) window.clearTimeout(intentTimer.current);
    intentTimer.current = window.setTimeout(() => {
      setIsEngaged(true);
      setActiveIndex(1);
    }, HOVER_INTENT_MS);
  };
  const handleMouseLeave = () => {
    if (!canHover || shouldReduceMotion) return;
    if (intentTimer.current) {
      window.clearTimeout(intentTimer.current);
      intentTimer.current = null;
    }
    setIsEngaged(false);
    setActiveIndex(0);
  };
  return <figure className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md shadow-cyan-950/10 ring-1 ring-inset ring-white sm:rounded-2xl" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-188" data-mp="n2mot8aw8ger">
      <figcaption className="flex min-h-11 items-center gap-2.5 border-b border-gray-200 bg-gray-50/90 px-3 py-2.5 sm:px-4" data-id="element-189" data-mp="2oxdmqezmxl5l">
        <span className="hidden gap-1.5 sm:flex" aria-hidden="true" data-id="element-190" data-mp="2mav0tu59jkbz">
          <span className="h-2 w-2 rounded-full bg-gray-300" data-id="element-191" data-mp="2mcz54d72qlcs" />
          <span className="h-2 w-2 rounded-full bg-gray-300" data-id="element-192" data-mp="2r8wnep1we1s7" />
          <span className="h-2 w-2 rounded-full bg-gray-300" data-id="element-193" data-mp="2vbjf8pykjmpe" />
        </span>
        <span className="relative min-w-0 flex-1 text-xs font-normal text-gray-500" data-id="element-194" data-mp="2v9gfvfbj0h80">
          <AnimatePresence mode="sync" initial={false} data-id="element-195" data-mp="309urt8g90bbb">
            <motion.span key={activeScreen.title} className="absolute inset-0 block truncate" initial={shouldReduceMotion ? false : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={shouldReduceMotion ? undefined : {
            opacity: 0
          }} transition={captionTransition} data-id="element-196" data-mp="23l6bcgk6kw40">
              {activeScreen.title}
            </motion.span>
          </AnimatePresence>
          <span className="invisible block truncate" aria-hidden="true" data-id="element-197" data-mp="1jvcs4qvj7pbe">
            {activeScreen.title}
          </span>
        </span>
      </figcaption>

      <div className="relative aspect-[4/3] overflow-hidden bg-gray-50" data-id="element-198" data-mp="1hby5qq3ojw24">
        <AnimatePresence mode="sync" initial={false} data-id="element-199" data-mp="1hb6ji5vz3aoi">
          <motion.img key={activeScreen.src} src={activeScreen.src} alt={activeScreen.alt} decoding="async" className="absolute inset-0 h-full w-full object-contain" initial={shouldReduceMotion ? false : {
          opacity: 0,
          scale: 1.015
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={shouldReduceMotion ? undefined : {
          opacity: 0,
          scale: 0.995
        }} transition={imageTransition} data-id="element-200" data-mp="1cclmsgfxj94t" />
        </AnimatePresence>

        <div className="absolute bottom-2.5 left-1/2 z-10 flex -translate-x-1/2 items-center rounded-full border border-gray-200/80 bg-white/95 px-1.5 shadow-sm" role="tablist" aria-label="Hero product screens" data-id="element-201" data-mp="on0zjlcqtppq">
          {screens.map((screen, index) => {
          const isActive = index === activeIndex;
          return <button key={screen.title} type="button" role="tab" aria-selected={isActive} aria-label={`Show ${screen.title}`} onClick={() => setActiveIndex(index)} className="flex h-9 items-center px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-1" data-id="element-202" data-mp="amzrnly29i0r" data-mpi={index}>
                <span aria-hidden="true" className={`h-1.5 rounded-full transition-all duration-300 ${isActive ? 'w-5 bg-gray-600' : 'w-1.5 bg-gray-300'}`} data-id="element-203" data-mp="3n85fpol2jcvx" />
              </button>;
        })}
        </div>
      </div>

      <p className="sr-only" aria-live="polite" data-id="element-204" data-mp="jrq0bti9ogdj">
        {activeScreen.title}, screen {activeIndex + 1} of {screens.length}
      </p>
    </figure>;
};